/**
 * @fileoverview Review API Contract (Contract-First Design)
 * @module types/contracts
 *
 * Complete API contract for the Review module.
 * Handles human-in-the-loop review workflow (pre-AI and post-AI).
 */

import { z } from 'zod';
import {
	createContract,
	createPaginatedSchema,
	ApiSuccessResponseSchema,
	ApiPaginatedResponseSchema,
} from './contract.utils';
import { TestResultSchema } from './test-result.contract';
import { AnalysisEntitySchema, AnalysisType } from './analysis.contract';

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Review Status Enum
 */
export enum ReviewStatus {
	PENDING = 'pending',
	IN_REVIEW = 'in_review',
	APPROVED = 'approved',
	REJECTED = 'rejected',
	NEEDS_REVISION = 'needs_revision',
}

/**
 * Review Stage Enum
 */
export enum ReviewStage {
	PRE_AI = 'pre_ai', // Review test results before AI analysis
	POST_AI = 'post_ai', // Review AI-generated analysis
}

/**
 * Review Priority Enum
 */
export enum ReviewPriority {
	LOW = 'low',
	MEDIUM = 'medium',
	HIGH = 'high',
	URGENT = 'urgent',
}

// ============================================================================
// SCHEMAS (Data Structure & Validation)
// ============================================================================

/**
 * Review Status Schema
 */
export const ReviewStatusSchema = z.nativeEnum(ReviewStatus);

/**
 * Review Stage Schema
 */
export const ReviewStageSchema = z.nativeEnum(ReviewStage);

/**
 * Review Priority Schema
 */
export const ReviewPrioritySchema = z.nativeEnum(ReviewPriority);

/**
 * Review Item Schema
 *
 * IMPORTANT: customerEmail is REQUIRED because:
 * 1. Needed for PDF delivery and email notifications
 * 2. Set during payment processing (payment.service.ts)
 * 3. Pulled from TestResult when creating review
 * 4. Used in all workflow steps (pre-AI, post-AI, email delivery)
 *
 * If customerEmail is missing, the workflow cannot proceed.
 * This is enforced at the orchestration level.
 *
 * and ISO strings (from API). This allows @ZodResponse to work correctly with DB entities.
 */
export const ReviewItemSchema = z.object({
	id: z.uuid(),
	testId: z.string(), // Format: test_<uuid>
	testResultId: z.uuid(),
	customerEmail: z.email(), // ✅ REQUIRED - needed for PDF delivery and email notifications
	status: ReviewStatusSchema,
	stage: ReviewStageSchema,
	priority: ReviewPrioritySchema,
	reviewNotes: z.string().nullable().optional(),
	reviewedBy: z.string().nullable().optional(),
	reviewedAt: z.iso.datetime().nullable().optional(),
	changesSummary: z.string().nullable().optional(),
	metadata: z.record(z.string(), z.unknown()).nullable().optional(),
	createdAt: z.iso.datetime(),
	updatedAt: z.iso.datetime(),
});
export type ReviewItemEntity = z.infer<typeof ReviewItemSchema>;

/**
 * Review History Entry Schema
 * Represents a single history entry in the review audit trail
 */
export const ReviewHistoryEntrySchema = z.object({
	id: z.string(),
	action: z.string(),
	reviewer: z.string(),
	timestamp: z.iso.datetime(),
	feedback: z.string().nullable(),
	status: ReviewStatusSchema.optional(),
});
export type ReviewHistoryEntry = z.infer<typeof ReviewHistoryEntrySchema>;

/**
 * Review History Response Schema
 * Array of history entries for a review
 */
export const ReviewHistoryResponseSchema = z.array(ReviewHistoryEntrySchema);
export type ReviewHistoryResponse = z.infer<typeof ReviewHistoryResponseSchema>;

/**
 * Submit for Pre-AI Review Request Schema
 */
export const SubmitForPreAIReviewRequestSchema = z.object({
	testId: z.string(), // Format: test_<uuid>
	customerEmail: z.email(),
});
export type SubmitForPreAIReviewRequest = z.infer<typeof SubmitForPreAIReviewRequestSchema>;

/**
 * Submit for Post-AI Review Request Schema
 */
export const SubmitForPostAIReviewRequestSchema = z.object({
	testId: z.string(), // Format: test_<uuid>
	customerEmail: z.email(),
});
export type SubmitForPostAIReviewRequest = z.infer<typeof SubmitForPostAIReviewRequestSchema>;

/**
 * Review Decision Request Schema
 */
export const ReviewDecisionRequestSchema = z.object({
	status: ReviewStatusSchema,
	reviewNotes: z.string().optional(),
	reviewedBy: z.string(),
	changesSummary: z.string().optional(),
});
export type ReviewDecisionRequest = z.infer<typeof ReviewDecisionRequestSchema>;

/**
 * Get Reviews Query Schema
 */
export const GetReviewsQuerySchema = z.object({
	status: ReviewStatusSchema.optional(),
	stage: ReviewStageSchema.optional(),
	priority: ReviewPrioritySchema.optional(),
	page: z.coerce.number().int().positive().default(1),
	limit: z.coerce.number().int().positive().max(100).default(20),
});
export type GetReviewsQuery = z.infer<typeof GetReviewsQuerySchema>;

/**
 * Paginated Reviews Response Schema
 */
export const PaginatedReviewsSchema = createPaginatedSchema(ReviewItemSchema);
export type PaginatedReviews = z.infer<typeof PaginatedReviewsSchema>;

/**
 * Review Statistics Schema
 */
export const ReviewStatisticsSchema = z.object({
	total: z.number().int().nonnegative(),
	pending: z.number().int().nonnegative(),
	inReview: z.number().int().nonnegative(),
	approved: z.number().int().nonnegative(),
	rejected: z.number().int().nonnegative(),
	needsRevision: z.number().int().nonnegative(),
	byStage: z.object({
		preAi: z.number().int().nonnegative(),
		postAi: z.number().int().nonnegative(),
	}),
	byPriority: z.object({
		low: z.number().int().nonnegative(),
		medium: z.number().int().nonnegative(),
		high: z.number().int().nonnegative(),
		urgent: z.number().int().nonnegative(),
	}),
});
export type ReviewStatistics = z.infer<typeof ReviewStatisticsSchema>;

// ============================================================================
// API RESPONSE WRAPPERS
// ============================================================================

/**
 * Review Item API Response
 * { success: true, data: ReviewItem, timestamp: string }
 */
export const ReviewItemApiResponseSchema = ApiSuccessResponseSchema(ReviewItemSchema);

/**
 * Paginated Reviews API Response
 * { success: true, data: ReviewItem[], meta: PaginationMeta, timestamp: string }
 */
export const PaginatedReviewsApiResponseSchema = ApiPaginatedResponseSchema(ReviewItemSchema);

/**
 * Review Statistics API Response
 * { success: true, data: ReviewStatistics, timestamp: string }
 */
export const ReviewStatisticsApiResponseSchema = ApiSuccessResponseSchema(ReviewStatisticsSchema);

/**
 * Review History API Response
 * { success: true, data: ReviewHistoryEntry[], timestamp: string }
 */
export const ReviewHistoryApiResponseSchema = ApiSuccessResponseSchema(
	ReviewHistoryResponseSchema,
);

/**
 * Review Snapshot Type Enum
 *
 * Represents different states of a review snapshot:
 * - ORIGINAL: Pre-AI review - original test data version
 * - AI_GENERATED: After AI analysis - analysis metadata version
 * - APPROVED: After post-AI review approval - approval metadata version
 * - SUBMITTED: After submit (before PDF) - submission metadata version
 * - PDF_GENERATED: After PDF generation - PDF metadata version
 */
export enum SnapshotType {
	ORIGINAL = 'original',
	AI_GENERATED = 'ai_generated',
	APPROVED = 'approved',
	SUBMITTED = 'submitted',
	PDF_GENERATED = 'pdf_generated',
}

/**
 * Review Snapshot Schema
 */
export const ReviewSnapshotSchema = z.object({
	id: z.uuid(),
	reviewId: z.uuid(),
	version: z.number().int().positive(),
	snapshotType: z.enum(SnapshotType),
	data: z.record(z.string(), z.unknown()),
	createdBy: z.string(),
	createdAt: z.iso.datetime(),
	comment: z.string().nullable(),
});
export type ReviewSnapshot = z.infer<typeof ReviewSnapshotSchema>;

/**
 * Create Snapshot Request Schema
 */
export const CreateSnapshotRequestSchema = z.object({
	data: z.record(z.string(), z.unknown()),
	snapshotType: z.nativeEnum(SnapshotType),
	comment: z.string().optional(),
});
export type CreateSnapshotRequest = z.infer<typeof CreateSnapshotRequestSchema>;

/**
 * Update Review Data Request Schema
 */
export const UpdateReviewDataRequestSchema = z.object({
	data: z.record(z.string(), z.unknown()),
	changesSummary: z.string(),
	reviewedBy: z.string(),
});
export type UpdateReviewDataRequest = z.infer<typeof UpdateReviewDataRequestSchema>;

/**
 * Pre-AI Snapshot Data Schema
 * Typed structure for pre-AI review snapshots
 */
export const PreAiSnapshotDataSchema = z.object({
	basicInfo: z.object({
		childName: z.string(),
		childAge: z.string(),
		childSchool: z.string(),
		childSituation: z.string(),
		childInterests: z.string().optional(),
		childGender: z.string(),
	}),
	scores: z.record(z.string(), z.number()),
	metadata: z.object({
		editedFields: z.array(z.string()).optional(),
		editedBy: z.string().optional(),
		editedAt: z.iso.datetime().optional(),
	}).optional(),
});
export type PreAiSnapshotData = z.infer<typeof PreAiSnapshotDataSchema>;

/**
 * Post-AI Snapshot Data Schema
 * Typed structure for post-AI review snapshots
 *
 * DEPRECATED: This schema is kept for backward compatibility.
 * New snapshots should use the specific snapshot type schemas below.
 */
export const PostAiSnapshotDataSchema = z.object({
	summary: z.string(),
	recommendations: z.string(),
	analysisTypes: z.record(z.string(), z.string()).optional(),
	metadata: z.object({
		aiModel: z.string().optional(),
		promptTemplate: z.string().optional(),
		generatedAt: z.iso.datetime().optional(),
	}).optional(),
});
export type PostAiSnapshotData = z.infer<typeof PostAiSnapshotDataSchema>;

/**
 * AI Generated Snapshot Data Schema
 * Stores analysis metadata after AI generation (NOT analysis content)
 * Content is stored in Analysis entities, snapshots store metadata only
 */
export const AiGeneratedSnapshotDataSchema = z.object({
	analysisMetadata: z.object({
		analysisCount: z.number().int().positive(),
		analysisTypes: z.array(z.enum(Object.values(AnalysisType) as [string, ...string[]])),
		model: z.string(),
		totalTokensUsed: z.number().int().nonnegative(),
		generatedAt: z.iso.datetime(),
	}),
	metadata: z.object({
		workflowId: z.string().uuid().optional(),
	}).optional(),
});
export type AiGeneratedSnapshotData = z.infer<typeof AiGeneratedSnapshotDataSchema>;

/**
 * Approved Snapshot Data Schema
 * Stores approval metadata after post-AI review approval
 */
export const ApprovedSnapshotDataSchema = z.object({
	approval: z.object({
		approvedAt: z.iso.datetime(),
		approvedBy: z.string().email(),
		approvalNotes: z.string().optional(),
	}),
	edits: z.object({
		editedAnalysisTypes: z.array(z.enum(Object.values(AnalysisType) as [string, ...string[]])).optional(),
		editedBy: z.string().email().optional(),
		editedAt: z.iso.datetime().optional(),
		editNotes: z.string().optional(),
	}).optional(),
	metadata: z.object({
		analysisCount: z.number().int().positive().optional(),
	}).optional(),
});
export type ApprovedSnapshotData = z.infer<typeof ApprovedSnapshotDataSchema>;

/**
 * Submitted Snapshot Data Schema
 * Stores submission metadata after admin submits for PDF generation
 */
export const SubmittedSnapshotDataSchema = z.object({
	submission: z.object({
		submittedAt: z.iso.datetime(),
		submittedBy: z.string().email(),
		submissionNotes: z.string().optional(),
	}),
	metadata: z.object({
		workflowId: z.string().uuid().optional(),
	}).optional(),
});
export type SubmittedSnapshotData = z.infer<typeof SubmittedSnapshotDataSchema>;

/**
 * PDF Generated Snapshot Data Schema
 * Stores PDF metadata after PDF generation
 */
export const PdfGeneratedSnapshotDataSchema = z.object({
	pdf: z.object({
		pdfId: z.uuid(),
		pdfUrl: z.string().url(),
		generatedAt: z.iso.datetime(),
		fileSize: z.number().int().positive(),
	}),
	metadata: z.object({
		emailSent: z.boolean().optional(),
		emailSentAt: z.iso.datetime().optional(),
	}).optional(),
});
export type PdfGeneratedSnapshotData = z.infer<typeof PdfGeneratedSnapshotDataSchema>;

/**
 * Review Full Response Schema (with snapshots, test result data, and analyses)
 *
 * Includes the related TestResult entity so admin can see and edit:
 * - basicInfo (childName, childAge, childSchool, childSituation)
 * - answers (raw test answers)
 * - scores (calculated test scores)
 * - testType, studentName, studentAge, school, situation
 *
 * Also includes Analysis entities for post-AI reviews so admin can see:
 * - All AI-generated analysis types (general, therapist, teacher, detailed, recommendations)
 * - Analysis content, status, model used, tokens consumed
 */
export const ReviewFullResponseSchema = z.object({
	review: ReviewItemSchema,
	testResult: TestResultSchema,
	analyses: z.array(AnalysisEntitySchema), // ✅ NEW: AI analysis data for post-AI reviews
	snapshots: z.array(ReviewSnapshotSchema),
	history: ReviewHistoryResponseSchema,
});
export type ReviewFullResponse = z.infer<typeof ReviewFullResponseSchema>;

/**
 * Diff Result Schema
 */
export const DiffResultSchema = z.object({
	added: z.record(z.string(), z.unknown()),
	removed: z.record(z.string(), z.unknown()),
	modified: z.record(z.string(), z.object({
		old: z.unknown(),
		new: z.unknown(),
	})),
});
export type DiffResult = z.infer<typeof DiffResultSchema>;

/**
 * Review Snapshot API Response
 */
export const ReviewSnapshotApiResponseSchema = ApiSuccessResponseSchema(ReviewSnapshotSchema);

/**
 * Review Full API Response
 */
export const ReviewFullApiResponseSchema = ApiSuccessResponseSchema(ReviewFullResponseSchema);

/**
 * Diff Result API Response
 */
export const DiffResultApiResponseSchema = ApiSuccessResponseSchema(DiffResultSchema);

/**
 * Delete Review API Response
 * { success: true, data: { message: string }, timestamp: string }
 */
const DeleteReviewDataSchema = z.object({ message: z.string() });
export const DeleteReviewApiResponseSchema = ApiSuccessResponseSchema(DeleteReviewDataSchema);

// ============================================================================
// API CONTRACT (Routes, Methods, Request/Response)
// ============================================================================

/**
 * Review API Contract
 *
 * Defines the complete API surface for the Review module.
 * Both frontend and backend use this as the single source of truth.
 */
export const REVIEW_API_CONTRACT = createContract('/review', {
	/**
	 * POST /review/submit-pre-ai
	 * Submit test result for pre-AI review
	 */
	submitPreAI: {
		method: 'POST' as const,
		path: '/submit-pre-ai',
		body: SubmitForPreAIReviewRequestSchema,
		response: ReviewItemApiResponseSchema,
	},

	/**
	 * POST /review/submit-post-ai
	 * Submit AI-generated analysis for post-AI review
	 *
	 * Returns complete review context (ReviewFullResponse) so admin can see:
	 * - Review metadata (status, stage, priority)
	 * - TestResult data (what was analyzed)
	 * - Snapshots (pre-AI vs post-AI changes)
	 * - History (audit trail)
	 * - AI analysis data (linked via Analysis entities)
	 *
	 * This follows the contract-first approach: API response includes all data
	 * needed for the admin UI to display and edit the post-AI review.
	 */
	submitPostAI: {
		method: 'POST' as const,
		path: '/submit-post-ai',
		body: SubmitForPostAIReviewRequestSchema,
		response: ReviewFullApiResponseSchema,
	},

	/**
	 * GET /review
	 * Get all review items (paginated, filterable)
	 */
	getAll: {
		method: 'GET' as const,
		path: '',
		query: GetReviewsQuerySchema,
		response: PaginatedReviewsApiResponseSchema,
	},

	/**
	 * GET /review/:reviewId
	 * Get a specific review item
	 */
	getById: {
		method: 'GET' as const,
		path: '/:reviewId',
		params: z.object({
			reviewId: z.uuid(),
		}),
		response: ReviewItemApiResponseSchema,
	},

	/**
	 * POST /review/:reviewId/decision
	 * Make a review decision (approve, reject, needs revision)
	 */
	makeDecision: {
		method: 'POST' as const,
		path: '/:reviewId/decision',
		params: z.object({
			reviewId: z.uuid(),
		}),
		body: ReviewDecisionRequestSchema,
		response: ReviewItemApiResponseSchema,
	},

	/**
	 * POST /review/:reviewId/submit
	 * Submit approved review for PDF generation and email delivery
	 *
	 * This endpoint is called after post-AI review approval to trigger
	 * the final workflow steps (PDF generation + email delivery).
	 *
	 * Emits REVIEW_EVENTS.SUBMITTED event to orchestration service.
	 */
	submit: {
		method: 'POST' as const,
		path: '/:reviewId/submit',
		params: z.object({
			reviewId: z.uuid(),
		}),
		body: z.object({
			submittedBy: z.string(),
		}),
		response: ReviewItemApiResponseSchema,
	},

	/**
	 * GET /review/statistics
	 * Get review statistics
	 */
	getStatistics: {
		method: 'GET' as const,
		path: '/statistics',
		response: ReviewStatisticsApiResponseSchema,
	},

	/**
	 * GET /review/:reviewId/history
	 * Get review history (audit trail)
	 */
	getHistory: {
		method: 'GET' as const,
		path: '/:reviewId/history',
		params: z.object({
			reviewId: z.uuid(),
		}),
		response: ReviewHistoryApiResponseSchema,
	},

	/**
 * DELETE /review/:reviewId
 * Delete a review item
 */
	delete: {
		method: 'DELETE' as const,
		path: '/:reviewId',
		params: z.object({
			reviewId: z.uuid(),
		}),
		response: DeleteReviewApiResponseSchema,
	},

	/**
	 * GET /review/:reviewId/full
	 * Get review with all snapshots and history
	 */
	getFull: {
		method: 'GET' as const,
		path: '/:reviewId/full',
		params: z.object({
			reviewId: z.uuid(),
		}),
		response: ReviewFullApiResponseSchema,
	},

	/**
	 * POST /review/:reviewId/snapshot
	 * Create a new snapshot (auto-save or manual save)
	 */
	createSnapshot: {
		method: 'POST' as const,
		path: '/:reviewId/snapshot',
		params: z.object({
			reviewId: z.uuid(),
		}),
		body: CreateSnapshotRequestSchema,
		response: ReviewSnapshotApiResponseSchema,
	},

	/**
	 * PATCH /review/:reviewId/data
	 * Update review data (creates snapshot automatically)
	 */
	updateData: {
		method: 'PATCH' as const,
		path: '/:reviewId/data',
		params: z.object({
			reviewId: z.uuid(),
		}),
		body: UpdateReviewDataRequestSchema,
		response: ReviewSnapshotApiResponseSchema,
	},

	/**
	 * GET /review/:reviewId/diff
	 * Get diff between two snapshots
	 */
	getDiff: {
		method: 'GET' as const,
		path: '/:reviewId/diff',
		params: z.object({
			reviewId: z.uuid(),
		}),
		query: z.object({
			from: z.coerce.number().int().positive(),
			to: z.coerce.number().int().positive(),
		}),
		response: DiffResultApiResponseSchema,
	},
});

/**
 * Route Constants for Controllers
 * Use these in @Controller() and @Get/@Post/@Delete decorators
 */
export const REVIEW_ROUTES = {
	base: REVIEW_API_CONTRACT.base,
	submitPreAI: REVIEW_API_CONTRACT.routes.submitPreAI.path,
	submitPostAI: REVIEW_API_CONTRACT.routes.submitPostAI.path,
	getAll: REVIEW_API_CONTRACT.routes.getAll.path,
	getById: REVIEW_API_CONTRACT.routes.getById.path,
	makeDecision: REVIEW_API_CONTRACT.routes.makeDecision.path,
	submit: REVIEW_API_CONTRACT.routes.submit.path,
	getStatistics: REVIEW_API_CONTRACT.routes.getStatistics.path,
	getHistory: REVIEW_API_CONTRACT.routes.getHistory.path,
	delete: REVIEW_API_CONTRACT.routes.delete.path,
	getFull: REVIEW_API_CONTRACT.routes.getFull.path,
	createSnapshot: REVIEW_API_CONTRACT.routes.createSnapshot.path,
	updateData: REVIEW_API_CONTRACT.routes.updateData.path,
	getDiff: REVIEW_API_CONTRACT.routes.getDiff.path,
};

// ============================================================================
// TYPE EXPORTS
// ============================================================================

/**
 * Export all types for use in frontend and backend
 */
export type ReviewApiContract = typeof REVIEW_API_CONTRACT;
